import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Import for react-draft-wysiwyg
import { EditorState, convertToRaw, convertFromRaw, ContentState } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import { BACKEND_URL, FILE_HOST } from '../../../Constants';

const CoursesDetail = () => {
  const { id: courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);

  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const [newModuleTitle, setNewModuleTitle] = useState('');
  const [newModuleDescriptionEditorState, setNewModuleDescriptionEditorState] = useState(() => EditorState.createEmpty());
  const [isModalOpen, setIsModalOpen] = useState(false);

  // States for editing a module
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editModuleTitle, setEditModuleTitle] = useState('');
  const [editModuleDescriptionEditorState, setEditModuleDescriptionEditorState] = useState(() => EditorState.createEmpty());
  const [editingModuleId, setEditingModuleId] = useState(null);

  // States for adding a page to a module
  const [isPageModalOpen, setIsPageModalOpen] = useState(false);
  const [newPageTitle, setNewPageTitle] = useState('');
  const [newPageContentEditorState, setNewPageContentEditorState] = useState(() => EditorState.createEmpty());
  const [currentModuleId, setCurrentModuleId] = useState(null);

  // States for course description editing
  const [isEditCourseDescriptionModalOpen, setIsEditCourseDescriptionModalOpen] = useState(false);
  const [editCourseDescriptionEditorState, setEditCourseDescriptionEditorState] = useState(() => EditorState.createEmpty());

  // States for Presentation
  const [isPresentationModalOpen, setIsPresentationModalOpen] = useState(false);
  const [newPresentationTitle, setNewPresentationTitle] = useState('');
  // Changed to store embed code
  const [newPresentationEmbedCode, setNewPresentationEmbedCode] = useState(''); 

  // States for editing Presentation
  const [isEditPresentationModalOpen, setIsEditPresentationModalOpen] = useState(false);
  const [editingPresentation, setEditingPresentation] = useState(null);
  const [editPresentationTitle, setEditPresentationTitle] = useState('');
  const [editPresentationEmbedCode, setEditPresentationEmbedCode] = useState('');

  // NEW STATES FOR EDITING COURSE DETAILS (including dates)
  // State to control the visibility of the "Edit Course Details" modal
  const [isEditCourseDetailsModalOpen, setIsEditCourseDetailsModalOpen] = useState(false);
  // State to hold the editable title for the course
  const [editCourseTitle, setEditCourseTitle] = useState('');
  // State to hold the editable license for the course
  const [editCourseLicense, setEditCourseLicense] = useState('');
  // State to hold the editable public status for the course
  const [editCourseIsPublic, setEditCourseIsPublic] = useState(false);
  // State to hold the editable start date for the course, formatted as YYYY-MM-DD
  const [editCourseStartDate, setEditCourseStartDate] = useState('');
  // State to hold the editable end date for the course, formatted as YYYY-MM-DD
  const [editCourseEndDate, setEditCourseEndDate] = useState('');

  // NEW STATE for Course Color
  const [courseColor, setCourseColor] = useState('#000000'); // Default to black

  // NEW STATES for Quiz Title
  const [newQuizTitle, setNewQuizTitle] = useState('');
  const [editQuizTitle, setEditQuizTitle] = useState('');

  
  const [existingPresentations, setExistingPresentations] = useState([]);
  const [presentationSource, setPresentationSource] = useState('upload');
  
  useEffect(() => {
    const fetchMedia = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/media`);
        const supportedFiles = response.data.files.filter((f) => {
          const ext = f.name.split('.').pop().toLowerCase();
          return ['ppt', 'pptx', 'mp4'].includes(ext);
        });
        setExistingPresentations(supportedFiles);
      } catch (err) {
        console.error('Failed to load media:', err);
      }
    };

    if (isPresentationModalOpen) {
      fetchMedia();
    }
  }, [isPresentationModalOpen]);


  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const response = await axios.get(
          `${ BACKEND_URL }/api/courses/${courseId}`
        );
        setCourse(response.data);
        // NEW: Initialize date states when course data is fetched
        // Converts ISO date string from backend to YYYY-MM-DD format for input type="date"
        setEditCourseStartDate(response.data.startDate ? new Date(response.data.startDate).toISOString().split('T')[0] : '');
        setEditCourseEndDate(response.data.endDate ? new Date(response.data.endDate).toISOString().split('T')[0] : '');
        if (response.data.color) {
            setCourseColor(response.data.color);
        }
      } catch (err) {
        console.error('Failed to load course:', err);
        toast.error('Failed to load course details.');
      }
    };

    if (courseId) {
      fetchCourse();
    }
  }, [courseId, navigate]);

  const handlePublishStatusChange = async (newStatus) => {
    setIsUpdatingStatus(true);
    try {
      const response = await axios.put(
        `${ BACKEND_URL }/api/courses/${courseId}/publish`,
        { published: newStatus }
      );
      setCourse(response.data);
      toast.success(
        `Course status updated to ${newStatus ? 'Published' : 'Draft'}`
      );
    } catch (error) {
      console.error('Error updating course status:', error);
      toast.error('Failed to update course status.');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleDeleteCourse = async () => {
    if (!window.confirm('Are you sure you want to delete this course? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${ BACKEND_URL }/api/courses/${courseId}`);
      toast.success('Course deleted successfully!');
      navigate('/dashboard/courses');
    } catch (error) {
      console.error('Error deleting course:', error);
      toast.error('Failed to delete course.');
    }
  };

  const handleAddModule = async () => {
    const descriptionToSave = JSON.stringify(convertToRaw(newModuleDescriptionEditorState.getCurrentContent()));

    try {
      const response = await axios.post(
        `${ BACKEND_URL }/api/courses/${courseId}/modules`,
        {
          title: newModuleTitle,
          description: descriptionToSave,
        }
      );
      setCourse(response.data);
      setNewModuleTitle('');
      setNewModuleDescriptionEditorState(EditorState.createEmpty());
      setIsModalOpen(false);
      toast.success('Module added successfully!');
    } catch (err) {
      console.error('Failed to add module', err);
      toast.error('Failed to add module.');
    }
  };

  const openModal = () => {
    setIsModalOpen(true);
    setNewModuleTitle('');
    setNewModuleDescriptionEditorState(EditorState.createEmpty());
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const openPageModal = (moduleId) => {
    setCurrentModuleId(moduleId);
    setNewPageTitle('');
    setNewPageContentEditorState(EditorState.createEmpty());
    setIsPageModalOpen(true);
  };

  const closePageModal = () => {
    setIsPageModalOpen(false);
  };

  const handleAddPage = async () => {
    const contentToSave = JSON.stringify(convertToRaw(newPageContentEditorState.getCurrentContent()));

    try {
      const response = await axios.post(
        `${ BACKEND_URL }/api/courses/${courseId}/modules/${currentModuleId}/pages`,
        {
          title: newPageTitle,
          content: contentToSave,
        }
      );
      setCourse(response.data);
      setNewPageTitle('');
      setNewPageContentEditorState(EditorState.createEmpty());
      closePageModal();
      toast.success('Page added successfully!');
    } catch (err) {
      console.error('Failed to add page:', err);
      toast.error('Failed to add page.');
    }
  };

  // This is for reordering modules
  const handleModuleDragStart = (e, index) => {
    e.dataTransfer.setData('moduleIndex', index.toString());
  };

  const handleModuleDrop = async (e, targetIndex) => {
    e.preventDefault();
    const draggedIndex = parseInt(e.dataTransfer.getData('moduleIndex'), 10);
    const updatedModules = [...course.modules];

    if (draggedIndex !== targetIndex) {
      const [draggedModule] = updatedModules.splice(draggedIndex, 1);
      updatedModules.splice(targetIndex, 0, draggedModule);

      setCourse({ ...course, modules: updatedModules });

      try {
        await axios.put(
          `${ BACKEND_URL }/api/courses/${courseId}/reorder-modules`,
          {
            moduleIds: updatedModules.map((m) => m._id),
          }
        );
        toast.success('Module order updated!');
      }
      catch (err) {
        console.error('Failed to update module order', err);
        toast.error('Failed to update module order.');
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const openEditModal = (module) => {
    setEditingModuleId(module._id);
    setEditModuleTitle(module.title);
    try {
      const contentState = convertFromRaw(JSON.parse(module.description));
      setEditModuleDescriptionEditorState(EditorState.createWithContent(contentState));
    } catch (e) {
      const blocksFromHtml = htmlToDraft(module.description || '');
      const { contentBlocks, entityMap } = blocksFromHtml;
      const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
      setEditModuleDescriptionEditorState(EditorState.createWithContent(contentState));
    }
    setIsEditModalOpen(true);
  };

  const handleUpdateModule = async () => {
    const descriptionToSave = JSON.stringify(convertToRaw(editModuleDescriptionEditorState.getCurrentContent()));

    try {
      const response = await axios.put(
        `${ BACKEND_URL }/api/courses/${courseId}/modules/${editingModuleId}`,
        {
          title: editModuleTitle,
          description: descriptionToSave,
        }
      );
      setCourse(response.data);
      setIsEditModalOpen(false);
      toast.success('Module updated!');
    } catch (err) {
      console.error('Failed to update module', err);
      toast.error('Failed to update module.');
    }
  };

  const handleDeleteModule = async (moduleId) => {
    if (!window.confirm('Are you sure you want to delete this module?')) return;

    try {
      const response = await axios.delete(
        `${ BACKEND_URL }/api/courses/${courseId}/modules/${moduleId}`
      );
      toast.success('Module marked as deleted');

      const updatedModules = course.modules.filter(
        (module) => module._id !== moduleId
      );
      setCourse({ ...course, modules: updatedModules });
    } catch (err) {
      console.error('Error deleting module:', err);
      toast.error('Error deleting module');
    }
  };

  const [isEditPageModalOpen, setIsEditPageModalOpen] = useState(false);
  const [editPageTitle, setEditPageTitle] = useState('');
  const [editPageContentEditorState, setEditPageContentEditorState] = useState(() => EditorState.createEmpty());
  const [editingPageId, setEditingPageId] = useState(null);

  const openEditPageModal = (module, page) => {
    setEditingModuleId(module._id);
    setEditingPageId(page._id);
    setEditPageTitle(page.title);

    try {
      const contentState = convertFromRaw(JSON.parse(page.content));
      setEditPageContentEditorState(EditorState.createWithContent(contentState));
    } catch (e) {
      const blocksFromHtml = htmlToDraft(page.content || '');
      const { contentBlocks, entityMap } = blocksFromHtml;
      const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
      setEditPageContentEditorState(EditorState.createWithContent(contentState));
    }
    setIsEditPageModalOpen(true);
  };

  const handleUpdatePage = async () => {
    const contentToSave = JSON.stringify(convertToRaw(editPageContentEditorState.getCurrentContent()));

    try {
      const response = await axios.put(
        `${ BACKEND_URL }/api/courses/${courseId}/modules/${editingModuleId}/pages/${editingPageId}`,
        {
          title: editPageTitle,
          content: contentToSave,
        }
      );

      setCourse(response.data);
      setIsEditPageModalOpen(false);
      toast.success('Page updated!');
    } catch (err) {
      console.error('Error updating page:', err);
      toast.error('Error updating page');
    }
  };

  const handleDeletePage = async (moduleId, pageId) => {
    if (!window.confirm('Are you sure you want to delete this page?')) return;

    try {
      const response = await axios.delete(
        `${ BACKEND_URL }/api/courses/${courseId}/modules/${moduleId}/pages/${pageId}`
      );
      console.log(response);
      setCourse(response.data);
      toast.success('Page marked as deleted');
    } catch (err) {
      console.error('Error deleting page:', err);
      toast.error('Error deleting page');
    }
  };

  // This is for reordering items (pages, quizzes, presentations) within a module
  const handleItemDragStart = (e, moduleIndex, itemIndex) => {
    e.dataTransfer.setData('moduleIndex', moduleIndex.toString()); // Ensure string
    e.dataTransfer.setData('itemIndex', itemIndex.toString());   // Ensure string
  };

  // This is for reordering items (pages, quizzes, presentations) within a module
  const handleItemDrop = async (e, targetModuleIndex, targetItemIndex) => {
    e.preventDefault(); // Important for drop to work

    // Parse the indices to ensure they are numbers
    const draggedModuleIndex = parseInt(e.dataTransfer.getData('moduleIndex'), 10);
    const draggedItemIndex = parseInt(e.dataTransfer.getData('itemIndex'), 10);

    const updatedModules = [...course.modules];

    // Basic validation for dragged item source
    if (isNaN(draggedModuleIndex) || draggedModuleIndex < 0 || draggedModuleIndex >= updatedModules.length ||
        !updatedModules[draggedModuleIndex] || !updatedModules[draggedModuleIndex].items ||
        isNaN(draggedItemIndex) || draggedItemIndex < 0 || draggedItemIndex >= updatedModules[draggedModuleIndex].items.length) {
      console.error("Invalid dragged item source indices or data:", { draggedModuleIndex, draggedItemIndex, updatedModules });
      toast.error("Error: Invalid item source detected during drag.");
      return;
    }

    // Basic validation for target drop location
    if (isNaN(targetModuleIndex) || targetModuleIndex < 0 || targetModuleIndex >= updatedModules.length ||
        !updatedModules[targetModuleIndex] || !updatedModules[targetModuleIndex].items) {
      console.error("Invalid target drop location indices or data:", { targetModuleIndex, updatedModules });
      toast.error("Error: Invalid drop target detected.");
      return;
    }

    const draggedItem = updatedModules[draggedModuleIndex].items[draggedItemIndex];

    // If dragging within the same module
    if (draggedModuleIndex === targetModuleIndex) {
      updatedModules[draggedModuleIndex].items.splice(draggedItemIndex, 1); // Remove from old position
      updatedModules[targetModuleIndex].items.splice(targetItemIndex, 0, draggedItem); // Insert into new position
    } else {
      // If dragging to a different module
      updatedModules[draggedModuleIndex].items.splice(draggedItemIndex, 1); // Remove from source module
      updatedModules[targetModuleIndex].items.splice(targetItemIndex, 0, draggedItem); // Add to target module
    }

    setCourse({ ...course, modules: updatedModules });

    // Send the updated order to the backend for the affected module(s)
    try {
      // If items were moved between modules, both modules' item orders need to be updated.
      // For simplicity here, we'll update the target module's order.
      // A more robust solution might involve updating both source and target modules if cross-module drag is allowed.
      await axios.put(
        `${ BACKEND_URL }/api/courses/${courseId}/modules/${updatedModules[targetModuleIndex]._id}/reorder-items`,
        {
          itemIds: updatedModules[targetModuleIndex].items.map((item) => item._id),
        }
      );
      // If the item was moved from a different module, also update the source module's order
      if (draggedModuleIndex !== targetModuleIndex) {
        await axios.put(
          `${ BACKEND_URL }/api/courses/${courseId}/modules/${updatedModules[draggedModuleIndex]._id}/reorder-items`,
          {
            itemIds: updatedModules[draggedModuleIndex].items.map((item) => item._id),
          }
        );
      }
      toast.success('Item order updated!');
    } catch (err) {
      console.error('Failed to update item order', err);
      toast.error('Failed to update item order.');
    }
  };


  // The single handleDragOver function at the top will serve both module and item drag operations.

  const [isQuizModalOpen, setIsQuizModalOpen] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState('');
  const [newOptions, setNewOptions] = useState(['', '', '', '']);
  const [correctAnswer, setCorrectAnswer] = useState('');

  const [editingQuestionIndex, setEditingQuestionIndex] = useState(null);
  const [editQuestionText, setEditQuestionText] = useState('');
  const [editOptions, setEditOptions] = useState(['', '', '', '']);
  const [editCorrectAnswer, setEditCorrectAnswer] = useState('');

  const handleAddQuestion = () => {
    if (!newQuestion || newOptions.some((opt) => !opt) || !correctAnswer) {
      toast.error('Please fill out the question, all options, and the correct answer.');
      return;
    }

    if (!newOptions.includes(correctAnswer)) {
      toast.error('The correct answer must be one of the provided options.');
      return;
    }

    const newQuizQuestion = {
      question: newQuestion,
      options: newOptions,
      correctAnswer: correctAnswer,
    };

    setQuestions([...questions, newQuizQuestion]);

    setNewQuestion('');
    setNewOptions(['', '', '', '']);
    setCorrectAnswer('');
  };

  const handleEditQuestion = (index, question) => {
    setEditingQuestionIndex(index);
    setEditQuestionText(question.question);
    setEditOptions([...question.options]);
    setEditCorrectAnswer(question.correctAnswer);
  };

  const handleSaveEditedQuestion = () => {
    const updatedQuestions = [...questions];
    updatedQuestions[editingQuestionIndex] = {
      question: editQuestionText,
      options: editOptions,
      correctAnswer: editCorrectAnswer,
    };
    setQuestions(updatedQuestions);
    setEditingQuestionIndex(null);
  };

  const handleDeleteLocalQuestion = (index) => {
    const updatedQuestions = questions.filter((_, i) => i !== index);
    setQuestions(updatedQuestions);
  };

  const handleSaveQuiz = async () => {
    if (!newQuizTitle) {
      toast.error('Please provide a title for the quiz.');
      return;
    }
    try {
      const newQuiz = {
        title: newQuizTitle, // Send the quiz title
        questions: questions,
      };

      const response = await axios.post(
        `${ BACKEND_URL }/api/courses/${courseId}/modules/${currentModuleId}/add-quiz`,
        newQuiz
      );

      setCourse(response.data);

      closeQuizModal();
      setQuestions([]);
      setNewQuizTitle('');
      toast.success('Quiz added successfully!');
    } catch (error) {
      console.error('Error saving quiz:', error);
      toast.error('Error saving quiz.');
    }
  };

  const openQuizModal = (moduleId) => {
    setCurrentModuleId(moduleId);
    setIsQuizModalOpen(true);
  };

  const closeQuizModal = () => {
    setIsQuizModalOpen(false);
    setQuestions([]);
    setNewQuizTitle('');
    setNewQuestion('');
    setNewOptions(['', '', '', '']);
    setCorrectAnswer('');
  };

  const handleDeleteQuiz = async (moduleId, quizItemId) => {
    if (!window.confirm('Are you sure you want to delete this quiz?')) return;

    try {
      const response = await axios.delete(
        `${ BACKEND_URL }/api/courses/${courseId}/modules/${moduleId}/quiz/${quizItemId}`
      );
      setCourse(response.data);
      toast.success('Quiz deleted successfully!');
    } catch (error) {
      console.error('Error deleting quiz:', error);
      toast.error('Failed to delete quiz.');
    }
  };

  const [isEditQuizModalOpen, setIsEditQuizModalOpen] = useState(false);
  const [editingQuizId, setEditingQuizId] = useState(null);
  const [editQuestions, setEditQuestions] = useState([]);

  const openEditQuizModal = (moduleId, quizItem) => {
    setEditingModuleId(moduleId);
    setEditingQuizId(quizItem._id);
    setEditQuizTitle(quizItem.quizTitle || ''); // Populate existing quiz title
    setEditQuestions(quizItem.quiz); // Set questions from item.quiz
    setIsEditQuizModalOpen(true);
  };

  const handleUpdateQuiz = async () => {
    if (!editQuizTitle) {
      toast.error('Please provide a title for the quiz.');
      return;
    }
    try {
      const updatedQuiz = {
        title: editQuizTitle, // Send the updated quiz title
        questions: editQuestions,
      };

      console.log('updatedQuiz',updatedQuiz)

      const response = await axios.put(
        `${ BACKEND_URL }/api/courses/${courseId}/modules/${editingModuleId}/quizzes/${editingQuizId}`,
        updatedQuiz
      );

      setIsEditQuizModalOpen(false);
      setEditQuestions([]);
      setEditQuizTitle(''); // Reset quiz title
      toast.success('Quiz updated successfully!');
      setCourse(response.data);
    } catch (error) {
      console.error('Error updating quiz:', error);
      toast.error('Failed to update quiz.');
    }
  };

  const handleAddEditQuizQuestion = () => {
    setEditQuestions(prevQuestions => [
      ...prevQuestions,
      { question: '', options: ['', '', '', ''], correctAnswer: '' },
    ]);
  };

  const handleEditQuestionChange = (index, field, value) => {
    const updatedQuestions = [...editQuestions];
    if (updatedQuestions[index]) {
      updatedQuestions[index][field] = value;
      setEditQuestions(updatedQuestions);
    }
  };

  const handleEditOptionChange = (questionIndex, optionIndex, value) => {
    const updatedQuestions = [...editQuestions];
    if (updatedQuestions[questionIndex]?.options) {
      const updatedOptions = [...updatedQuestions[questionIndex].options];
      updatedOptions[optionIndex] = value;
      updatedQuestions[questionIndex].options = updatedOptions;
      setEditQuestions(updatedQuestions);
    }
  };

  const handleDeleteEditQuizQuestion = (indexToDelete) => {
    setEditQuestions(prevQuestions =>
      prevQuestions.filter((_, index) => index !== indexToDelete)
    );
  };

  // Open Edit Course Description Modal
  const openEditCourseDescriptionModal = () => {
    let initialEditorState = EditorState.createEmpty();
    if (course.description) {
      try {
        const contentState = convertFromRaw(JSON.parse(course.description));
        initialEditorState = EditorState.createWithContent(contentState);
      } catch (e) {
        const blocksFromHtml = htmlToDraft(course.description);
        if (blocksFromHtml.contentBlocks) {
          const { contentBlocks, entityMap } = blocksFromHtml;
          const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
          initialEditorState = EditorState.createWithContent(contentState);
        }
      }
    }
    setEditCourseDescriptionEditorState(initialEditorState);
    setIsEditCourseDescriptionModalOpen(true);
  };

  // Handle Update Course Description
  const handleUpdateCourseDescription = async () => {
    const descriptionToSave = JSON.stringify(convertToRaw(editCourseDescriptionEditorState.getCurrentContent()));
    try {
      const response = await axios.put(
        `${ BACKEND_URL }/api/courses/${courseId}`,
        { description: descriptionToSave }
      );
      setCourse(response.data);
      toast.success('Course description updated successfully!');
      setIsEditCourseDescriptionModalOpen(false);
    } catch (error) {
      console.error('Error updating course description:', error);
      toast.error('Failed to update course description.');
    }
  };

  // Functions for Presentation
  const openPresentationModal = (moduleId) => {
    setCurrentModuleId(moduleId);
    setNewPresentationTitle('');
    setNewPresentationEmbedCode(''); // Reset embed code
    setIsPresentationModalOpen(true);
  };

  const closePresentationModal = () => {
    setIsPresentationModalOpen(false);
  };

  const handleAddPresentation = async () => {
    if (!newPresentationTitle || !newPresentationEmbedCode) {
      toast.error('Please provide both a title and the embed code for the presentation.');
      return;
    }

    try {
      const response = await axios.post(
        `${ BACKEND_URL }/api/courses/${courseId}/modules/${currentModuleId}/presentations`,
        { title: newPresentationTitle, embedCode: newPresentationEmbedCode } // Send embedCode
      );
      setCourse(response.data);
      closePresentationModal();
      toast.success('Presentation added successfully!');
    } catch (err) {
      console.error('Error adding presentation:', err);
      toast.error('Failed to add presentation.');
    }
  };

  const openEditPresentationModal = (module, presentationItem) => {
    setEditingModuleId(module._id);
    setEditingPresentation(presentationItem);
    setEditPresentationTitle(presentationItem.presentation.title);
    setEditPresentationEmbedCode(presentationItem.presentation.embedCode); // Set embed code
    setIsEditPresentationModalOpen(true);
  };

  const handleUpdatePresentation = async () => {
    if (!editPresentationTitle || !editPresentationEmbedCode) {
      toast.error('Please provide both a title and the embed code for the presentation.');
      return;
    }

    if (!editingPresentation?._id || !editingModuleId) {
      toast.error('Error: Missing presentation or module ID for update.');
      return;
    }

    try {
      const response = await axios.put(
        `${ BACKEND_URL }/api/courses/${courseId}/modules/${editingModuleId}/presentations/${editingPresentation._id}`,
        { title: editPresentationTitle, embedCode: editPresentationEmbedCode } // Send embedCode
      );
      setCourse(response.data);
      toast.success('Presentation updated successfully!');
      setIsEditPresentationModalOpen(false);
      setEditingPresentation(null);
    } catch (error) {
      console.error('Error updating presentation:', error);
      toast.error('Failed to update presentation.');
    }
  };

  const handleDeletePresentation = async (moduleId, presentationItemId) => {
    if (!window.confirm('Are you sure you want to delete this presentation?')) return;
    try {
      const response = await axios.delete(
        `${ BACKEND_URL }/api/courses/${courseId}/modules/${moduleId}/presentations/${presentationItemId}`
      );
      setCourse(response.data);
      toast.success('Presentation deleted successfully!');
    } catch (error) {
      console.error('Error deleting presentation:', error);
      toast.error('Failed to delete presentation.');
    }
  };

  // NEW FUNCTIONS FOR EDITING COURSE DETAILS
  /**
   * Opens the modal for editing general course details.
   * Initializes the state variables for title, license, public status, start date, and end date
   * with the current course's values. Dates are formatted for the input type="date".
   */
  const openEditCourseDetailsModal = () => {
    setEditCourseTitle(course.title);
    setEditCourseLicense(course.license);
    setEditCourseIsPublic(course.isPublic);
    // Format dates to YYYY-MM-DD for input type="date"
    setEditCourseStartDate(course.startDate ? new Date(course.startDate).toISOString().split('T')[0] : '');
    setEditCourseEndDate(course.endDate ? new Date(course.endDate).toISOString().split('T')[0] : '');
    setIsEditCourseDetailsModalOpen(true);
  };

  // NEW FUNCTION: Handle Course Color Change
  const handleCourseColorChange = async (e) => {
    const newColor = e.target.value;
    setCourseColor(newColor);
    try {
        const response = await axios.put(
            `${BACKEND_URL}/api/courses/${courseId}`,
            { color: newColor } // Send only the color for update
        );
        setCourse(response.data); // Update the course state with the new color
        toast.success('Course color updated successfully!');
    } catch (error) {
        console.error('Error updating course color:', error);
        toast.error('Failed to update course color.');
    }
  };

  /**
   * Handles the submission of updated course details.
   * Sends a PUT request to the backend with the new title, license, public status, start date, and end date.
   * Shows a success or error toast message.
   */
  const handleUpdateCourseDetails = async () => {
    // Basic validation for required fields
    if (!editCourseTitle || !editCourseLicense) {
      toast.error('Course Title and License are required.');
      return;
    }

    try {
      const response = await axios.put(
        `${ BACKEND_URL }/api/courses/${courseId}`,
        {
          title: editCourseTitle,
          license: editCourseLicense,
          isPublic: editCourseIsPublic,
          startDate: editCourseStartDate || null, // Send null if the date input is empty
          endDate: editCourseEndDate || null,     // Send null if the date input is empty
        }
      );
      setCourse(response.data); // Update the local course state with the response from the backend
      toast.success('Course details updated successfully!');
      setIsEditCourseDetailsModalOpen(false); // Close the modal on success
    } catch (error) {
      console.error('Error updating course details:', error);
      toast.error('Failed to update course details: ' + (error.response?.data?.message || error.message));
    }
  };


  if (!course) return <p>Loading course...</p>;

  // Function to render description (for course and modules)
  const renderDescription = (descriptionContent) => {
    let htmlContent = '';
    try {
      const rawContent = JSON.parse(descriptionContent);
      htmlContent = draftToHtml(rawContent);
    } catch (e) {
      htmlContent = descriptionContent; // Fallback to plain text/HTML
    }
    return <div dangerouslySetInnerHTML={{ __html: htmlContent }} />;
  };
  
  // Helper to format date for display in a readable format (e.g., MM/DD/YYYY)
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return 'N/A'; // Return 'N/A' if dateString is empty or null
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date'; // Handle invalid date strings
    return date.toLocaleDateString(); // Adjust format as needed (e.g., 'MM/DD/YYYY')
  };

  const handleExistingPresentationSelect = (e) => {
    const selectedFileName = e.target.value;
    if (!selectedFileName) return;
  
    const fileUrl = `${FILE_HOST}/uploads/${selectedFileName}`;
    const ext = selectedFileName.split('.').pop().toLowerCase();
    let embedCode = '';
  
    if (ext === 'ppt' || ext === 'pptx') {
      const encodedUrl = encodeURIComponent(fileUrl);
      embedCode = `<iframe src="https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}" width="100%" height="600px" frameborder="0" title="PowerPoint Viewer" style="border-radius: 6px;"></iframe>`;
    } else if (ext === 'mp4') {
      embedCode = `<video src="${fileUrl}" controls width="100%" height="auto" style="border-radius: 6px;"></video>`;
    } else {
      toast.error('Unsupported file type. Only .pptx and .mp4 are supported.');
      return;
    }
  
    setNewPresentationEmbedCode(embedCode);
    toast.success(`${ext.toUpperCase()} embed code generated!`);
  };

  const handlePresentationFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
  
    const formData = new FormData();
    formData.append('file', file);
  
    try {
      const response = await axios.post(`${BACKEND_URL}/api/media/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
  
      const uploadedFile = response.data.file;
      const fileUrl = uploadedFile.url.startsWith('http')
        ? uploadedFile.url
        : `${FILE_HOST}${uploadedFile.url}`;
  
      const ext = uploadedFile.name.split('.').pop().toLowerCase();
      let embedCode = '';
  
      if (ext === 'ppt' || ext === 'pptx') {
        const encodedUrl = encodeURIComponent(fileUrl);
        embedCode = `<iframe src="https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}" width="100%" height="600px" frameborder="0" title="PowerPoint Viewer" style="border-radius: 6px;"></iframe>`;
      } else if (ext === 'mp4') {
        embedCode = `<video src="${fileUrl}" controls width="100%" height="auto" style="border-radius: 6px;"></video>`;
      } else {
        toast.error('Unsupported file type. Only .pptx and .mp4 are supported.');
        return;
      }
  
      setNewPresentationEmbedCode(embedCode);
      toast.success(`${ext.toUpperCase()} file uploaded and embed code generated!`);
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error('Failed to upload file.');
    }
  };


  return (
    <div className="course-detail-container">
      <div className="row pt-2 pb-2 align-items-center">
        <div className="col-sm-9">
          <h2 className="course-title">{course.title}</h2>
          {/* NEW: Display Start and End Dates */}
          <p className="text-muted">
            Start Date: {formatDateForDisplay(course.startDate)} | End Date: {formatDateForDisplay(course.endDate)}
          </p>
          {/* Render Course Description */}
          <div className="course-description-display">
            {renderDescription(course.description)}
            <button
              onClick={openEditCourseDescriptionModal}
              className="btn btn-sm btn-outline-secondary mt-2 me-2"
            >
              Edit Description
            </button>
            {/* NEW: Button to open general course details edit modal */}
            <button
              onClick={openEditCourseDetailsModal}
              className="btn btn-sm btn-outline-primary mt-2"
            >
              Edit Course Details
            </button>
          </div>
        </div>
        <div className="col-sm-3 d-flex align-items-center justify-content-sm-end">
          <div className="course-status">
            <strong>Status:</strong>{' '}
            <span className={course.published ? 'published' : 'draft'}>
              {course.published ? 'Published' : 'Draft'}
            </span>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12 col-lg-3 order-lg-last">
          <div className="course-settings">
            <h3>Course Settings</h3>

            {/* NEW: Course Color Picker */}
            <div className="mb-3">
                <label htmlFor="courseColorPicker" className="form-label">Course Color:</label>
                <input
                    type="color"
                    id="courseColorPicker"
                    className="form-control form-control-color"
                    value={courseColor}
                    onChange={handleCourseColorChange}
                    title="Choose your course color"
                />
            </div>

            <button
              onClick={() => handlePublishStatusChange(true)}
              disabled={course.published || isUpdatingStatus}
              className={
                course.published
                  ? 'btn btn-success disabled'
                  : 'btn btn-primary'
              }
            >
              {isUpdatingStatus ? 'Publishing...' : 'Publish'}
            </button>
            <button
              onClick={() => handlePublishStatusChange(false)}
              disabled={!course.published || isUpdatingStatus}
              className={
                !course.published
                  ? 'btn btn-secondary disabled'
                  : 'btn btn-warning'
              }
            >
              {isUpdatingStatus ? 'Saving as Draft...' : 'Set as Draft'}
            </button>
            <button
              onClick={handleDeleteCourse}
              className="btn btn-danger mt-2 w-100"
            >
              Delete Course
            </button>
          </div>

          <div className="card">
            <div className="card-body">
                <div className="media">
                <div className="align-self-center">
                    <span>Total Students</span>
                </div>
                <div className="media-body text-right">
                    <h4 className="text-dark">{course?.totalStudents || 0}</h4>
                </div>
                </div>
            </div>
            </div>

<div className="card">
  <div className="card-body">
    <div className="media">
      <div className="align-self-center">
        <span>Students Took Course</span>
      </div>
      <div className="media-body text-right">
        <h4 className="text-dark">{course?.studentsTookCourse || 0}</h4>
      </div>
    </div>
  </div>
</div>

<div className="card">
  <div className="card-body">
    <div className="media">
      <div className="align-self-center">
        <span>Current Active Students</span>
      </div>
      <div className="media-body text-right">
        <h4 className="text-dark">{course?.activeStudents || 0}</h4>
      </div>
    </div>
  </div>
</div>

<div className="card">
  <div className="card-body">
    <div className="media">
      <div className="align-self-center">
        <span>View Applicants</span>
      </div>
      <div className="media-body text-right">
        <Link to={`/dashboard/courses/applicants/${courseId}`} className='btn btn-outline-secondary btn-sm waves-effect waves-light m-1'>View</Link>
      </div>
    </div>
  </div>
</div>


        </div>
        <div className="col-12 col-lg-9 order-lg-first">
          <h3 className="modules-heading">Modules:</h3>
          <div className="modules-list">
            {course.modules?.map(
              (module, index) =>
                !module.deleted && (
                  <div
                    key={module._id}
                    draggable
                    onDragStart={(e) => handleModuleDragStart(e, index)}
                    onDrop={(e) => handleModuleDrop(e, index)}
                    onDragOver={handleDragOver}
                    className="draggable-module"
                  >
                    <h4 className="module-title">{module.title}</h4>
                    {/* Render HTML content for module description */}
                    {renderDescription(module.description)}
                    <div className="module-actions">
                      <button
                        onClick={() => openEditModal(module)}
                        className="btn btn-sm btn-warning"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteModule(module._id)}
                        className="btn btn-sm btn-danger"
                      >
                        Delete
                      </button>
                      <button
                        onClick={() => openPageModal(module._id)}
                        className="btn btn-sm btn-info"
                      >
                        Add Page
                      </button>
                      <button
                        onClick={() => openQuizModal(module._id)}
                        className="btn btn-sm btn-primary"
                      >
                        Add Quiz
                      </button>
                      {/* Add Presentation Button */}
                      <button
                        onClick={() => openPresentationModal(module._id)}
                        className="btn btn-sm btn-success"
                      >
                        Add Presentation
                      </button>
                    </div>

                    {/* List items in the module (pages, quizzes, presentations) */}
                    {module.items?.map((item, itemIndex) => {
                      if (item.type === 'page' && !item.page.deleted) {
                          let pageContentHtml = '';
                          try {
                            const rawContent = JSON.parse(item.page.content);
                            pageContentHtml = draftToHtml(rawContent);
                          } catch (e) {
                            pageContentHtml = item.page.content;
                          }
                        return (
                          <div
                            key={item._id} // Use item._id for unique key
                            draggable
                            onDragStart={(e) =>
                              handleItemDragStart(e, index, itemIndex) // Use handleItemDragStart
                            }
                            onDrop={(e) => handleItemDrop(e, index, itemIndex)} // Use handleItemDrop
                            onDragOver={handleDragOver}
                            className="draggable-page"
                          >
                            <h5 className="page-title">{item.page.title}</h5>
                            <div className="page-content" dangerouslySetInnerHTML={{ __html: pageContentHtml }} />
                            <div className="page-actions">
                              <button
                                onClick={() =>
                                  openEditPageModal(module, item.page)
                                }
                                className="btn btn-sm btn-warning"
                              >
                                Edit Page
                              </button>
                              <button
                                onClick={() =>
                                  handleDeletePage(module._id, item.page._id)
                                }
                                className="btn btn-sm btn-danger"
                              >
                                Delete Page
                              </button>
                            </div>
                          </div>
                        )
                      } else if (item.type === 'quiz') {
                        return (
                          <div className="quiz-item" key={item._id}
                            draggable
                            onDragStart={(e) => handleItemDragStart(e, index, itemIndex)}
                            onDrop={(e) => handleItemDrop(e, index, itemIndex)}
                            onDragOver={handleDragOver}
                          >
                            {item.quizTitle ? (
                            <h5>Quiz: {item.quizTitle}</h5>
                          ) : (
                            <h5>Quiz</h5>
                          )}
                            {item.quiz?.map((q, idx) => (
                              <div key={idx}>
                                <p className="quiz-question">
                                  <strong>Question {idx + 1}:</strong>{' '}
                                  {q.question}
                                </p>
                                <ul className="quiz-options-list">
                                  {q.options.map((option, i) => (
                                    <li key={i} className="quiz-option-item">
                                      {option}{' '}
                                      {option === q.correctAnswer && (
                                        <strong className="correct-answer">
                                          (Correct)
                                        </strong>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                            <div className="quiz-actions">
                              <button
                                onClick={() =>
                                  openEditQuizModal(module._id, item)
                                }
                                className="btn btn-primary"
                              >
                                Edit Quiz
                              </button>
                              <button
                                onClick={() =>
                                  handleDeleteQuiz(module._id, item._id)
                                }
                                className="btn btn-danger"
                              >
                                Delete Quiz
                              </button>
                            </div>
                          </div>
                        )
                      } else if (item.type === 'presentation') {
                        return (
                          <div className="presentation-item card mb-3" key={item._id}
                            draggable
                            onDragStart={(e) => handleItemDragStart(e, index, itemIndex)}
                            onDrop={(e) => handleItemDrop(e, index, itemIndex)}
                            onDragOver={handleDragOver}
                          >
                            <div className="card-body">
                              <h5 className="card-title">{item.presentation.title}</h5>
                              {/* Render embed code directly */}
                              {item.presentation.embedCode && (
                                <div className="embed-responsive embed-responsive-16by9">
                                  <div dangerouslySetInnerHTML={{ __html: item.presentation.embedCode }} />
                                </div>
                              )}
                              <div className="presentation-actions mt-3">
                                <button
                                  onClick={() => openEditPresentationModal(module, item)}
                                  className="btn btn-sm btn-warning me-2"
                                >
                                  Edit Presentation
                                </button>
                                <button
                                  onClick={() => handleDeletePresentation(module._id, item._id)}
                                  className="btn btn-sm btn-danger"
                                >
                                  Delete Presentation
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      }

                      return null;
                    })}
                  </div>
                )
            )}
          </div>

          <button onClick={openModal} className="add-module-button">
            Add New Module
          </button>
        </div>
      </div>

      {isModalOpen && (
  <div className="modal" style={{ display: 'block' }}>
    <div className="modal-dialog">
      <div className="modal-content">
        <div className="modal-header">
          <h5 className="modal-title">Add New Module</h5>
          <button type="button" class="close" data-dismiss="modal" aria-label="Close"
              onClick={ closeModal }>
              <span aria-hidden="true">×</span>
            </button>
        </div>
        <div className="modal-body">
        <div style={{ padding: '20px' }}>
          <div className="mb-3">
            <label htmlFor="moduleTitle" className="form-label">Module Title:</label>
            <input
              type="text"
              className="form-control"
              id="moduleTitle"
              placeholder="Enter module title"
              value={newModuleTitle}
              onChange={(e) => setNewModuleTitle(e.target.value)}
            />
          </div>
          <div className="mb-3">
            <label htmlFor="moduleDescription" className="form-label">Module Description:</label>
            <Editor
              editorState={newModuleDescriptionEditorState}
              wrapperClassName="demo-wrapper"
              editorClassName="demo-editor"
              onEditorStateChange={setNewModuleDescriptionEditorState}
            />
          </div>
          </div>
          </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={closeModal}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleAddModule}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  </div>
)}
      {isEditModalOpen && (
  <div className="modal" style={{ display: 'block' }}>
    <div className="modal-dialog">
      <div className="modal-content">
        <div className="modal-header">
          <h5 className="modal-title">Edit Module</h5>
          
          <button type="button" class="close" data-dismiss="modal" aria-label="Close"
               onClick={() => setIsEditModalOpen(false)}>
              <span aria-hidden="true">×</span>
            </button>
        </div>
        <div className="modal-body">
          <div style={{ padding: '20px' }}>
            <div className="mb-3">
              <label htmlFor="editModuleTitle" className="form-label">Module Title:</label>
              <input
                type="text"
                className="form-control"
                id="editModuleTitle"
                placeholder="Enter module title"
                value={editModuleTitle}
                onChange={(e) => setEditModuleTitle(e.target.value)}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="editModuleDescription" className="form-label">Module Description:</label>
              <Editor
                editorState={editModuleDescriptionEditorState}
                wrapperClassName="demo-wrapper"
                editorClassName="demo-editor"
                onEditorStateChange={setEditModuleDescriptionEditorState}
              />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setIsEditModalOpen(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleUpdateModule}
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  </div>
)}

{isPageModalOpen && (
  <div className="modal" style={{ display: 'block' }}>
    <div className="modal-dialog modal-lg">
      <div className="modal-content">
        <div className="modal-header">
          <h5 className="modal-title">Add New Page</h5>
<button type="button" class="close" data-dismiss="modal" aria-label="Close"
               onClick={closePageModal}>
              <span aria-hidden="true">×</span>
            </button>
        </div>
        <div className="modal-body">
          <div style={{ padding: '20px' }}>
            <div className="mb-3">
              <label htmlFor="pageTitle" className="form-label">Page Title:</label>
              <input
                type="text"
                className="form-control"
                id="pageTitle"
                placeholder="Enter page title"
                value={newPageTitle}
                onChange={(e) => setNewPageTitle(e.target.value)}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="pageContent" className="form-label">Page Content:</label>
              <Editor
                editorState={newPageContentEditorState}
                wrapperClassName="demo-wrapper"
                editorClassName="demo-editor"
                onEditorStateChange={setNewPageContentEditorState}
              />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={closePageModal}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleAddPage}
          >
            Save Page
          </button>
        </div>
      </div>
    </div>
  </div>
)}

{isEditPageModalOpen && (
  <div className="modal" style={{ display: 'block' }}>
    <div className="modal-dialog modal-lg">
      <div className="modal-content">
        <div className="modal-header">
          <h5 className="modal-title">Edit Page</h5>
          
<button type="button" class="close" data-dismiss="modal" aria-label="Close"
               onClick={() => setIsEditPageModalOpen(false)}>
              <span aria-hidden="true">×</span>
            </button>
        </div>
        <div className="modal-body">
          <div style={{ padding: '20px' }}>
            <div className="mb-3">
              <label htmlFor="editPageTitle" className="form-label">Page Title:</label>
              <input
                type="text"
                className="form-control"
                id="editPageTitle"
                placeholder="Enter page title"
                value={editPageTitle}
                onChange={(e) => setEditPageTitle(e.target.value)}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="editPageContent" className="form-label">Page Content:</label>
              <Editor
                editorState={editPageContentEditorState}
                wrapperClassName="demo-wrapper"
                editorClassName="demo-editor"
                onEditorStateChange={setEditPageContentEditorState}
              />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setIsEditPageModalOpen(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleUpdatePage}
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  </div>
)}

      {isQuizModalOpen && (
        <div className="modal" style={{ display: 'block' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Quiz</h5>
<button type="button" class="close" data-dismiss="modal" aria-label="Close"
               onClick={closeQuizModal}>
              <span aria-hidden="true">×</span>
            </button>
              </div>
              <div className="modal-body">
                
          <div style={{ padding: '20px' }}>
                {/* NEW: Quiz Title Input */}
                <div className="mb-3">
                  <label htmlFor="newQuizTitle" className="form-label">Quiz Title:</label>
                  <input
                    type="text"
                    className="form-control"
                    id="newQuizTitle"
                    placeholder="Enter quiz title"
                    value={newQuizTitle}
                    onChange={(e) => setNewQuizTitle(e.target.value)}
                    required
                  />
                </div>
                <h4>Add New Question</h4>
                <input
                  type="text"
                  className="form-control mb-2"
                  placeholder="New Question"
                  value={newQuestion}
                  onChange={(e) => setNewQuestion(e.target.value)}
                />
                {newOptions.map((option, index) => (
                  <input
                    key={index}
                    type="text"
                    className="form-control mb-2"
                    placeholder={`Option ${index + 1}`}
                    value={option}
                    onChange={(e) => {
                      const newOpts = [...newOptions]
                      newOpts[index] = e.target.value
                      setNewOptions(newOpts)
                    }}
                  />
                ))}
                <input
                  type="text"
                  className="form-control mb-2"
                  placeholder="Correct Answer"
                  value={correctAnswer}
                  onChange={(e) => setCorrectAnswer(e.target.value)}
                />
                <button
                  className="btn btn-primary mb-3"
                  onClick={handleAddQuestion}
                >
                  Add Question
                </button>

                {questions.length > 0 && (
                  <div>
                    <h4>Current Questions:</h4>
                    {questions.map((q, index) => (
                      <div key={index} className="card mb-3">
                        <div className="card-body">
                          {editingQuestionIndex === index ? (
                            <div>
                              <input
                                type="text"
                                className="form-control mb-2"
                                value={editQuestionText}
                                onChange={(e) =>
                                  setEditQuestionText(e.target.value)
                                }
                              />
                              {editOptions.map((opt, optIndex) => (
                                <input
                                  key={optIndex}
                                  type="text"
                                  className="form-control mb-2"
                                  value={opt}
                                  onChange={(e) => {
                                    const updatedOptions = [...editOptions]
                                    updatedOptions[optIndex] = e.target.value
                                    setEditOptions(updatedOptions)
                                  }}
                                />
                              ))}
                              <input
                                type="text"
                                className="form-control mb-2"
                                value={editCorrectAnswer}
                                onChange={(e) =>
                                  setEditCorrectAnswer(e.target.value)
                                }
                              />
                              <button
                                className="btn btn-success me-2"
                                onClick={handleSaveEditedQuestion}
                              >
                                Save
                              </button>
                               <button
                                className="btn btn-secondary"
                                onClick={() => setEditingQuestionIndex(null)}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div>
                              <p className="card-text">
                                <strong>Question {index + 1}:</strong>{' '}
                                {q.question}
                              </p>
                              <ul className="list-unstyled">
                                {q.options.map((opt, i) => (
                                  <li key={i}>
                                    Option {i + 1}: {opt}{' '}
                                    {opt === q.correctAnswer ? '(Correct)' : ''}
                                  </li>
                                ))}
                              </ul>
                              <button
                                className="btn btn-sm btn-warning me-2"
                                onClick={() => handleEditQuestion(index, q)}
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleDeleteLocalQuestion(index)}
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeQuizModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveQuiz}
                >
                  Save Quiz
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

{isEditQuizModalOpen && (
  <div className="modal" style={{ display: 'block' }}>
    <div className="modal-dialog modal-lg">
      <div className="modal-content">
        <div className="modal-header">
          <h5 className="modal-title">Edit Quiz</h5>
<button type="button" class="close" data-dismiss="modal" aria-label="Close"
               onClick={() => setIsEditQuizModalOpen(false)}>
              <span aria-hidden="true">×</span>
            </button>
        </div>
        <div className="modal-body">
          {/* NEW: Edit Quiz Title Input */}
          <div className="mb-3">
            <label htmlFor="editQuizTitle" className="form-label">Quiz Title:</label>
            <input
              type="text"
              className="form-control"
              id="editQuizTitle"
              placeholder="Enter quiz title"
              value={editQuizTitle}
              onChange={(e) => setEditQuizTitle(e.target.value)}
              required
            />
          </div>
          <button
            type="button"
            className="btn btn-success mb-3"
            onClick={handleAddEditQuizQuestion}
          >
            Add Question
          </button>
          {editQuestions.map((question, index) => (
            <div key={index} className="card mb-3">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <h4>Question {index + 1}</h4>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDeleteEditQuizQuestion(index)}
                  >
                    Delete
                  </button>
                </div>
                <div className="mb-2">
                  <label htmlFor="questionText" className="form-label">Question Text:</label>
                  <input
                    type="text"
                    className="form-control"
                    id="questionText"
                    value={editQuestions[index]?.question || ''}
                    onChange={(e) => handleEditQuestionChange(index, 'question', e.target.value)}
                  />
                </div>
                {editQuestions[index]?.options.map((option, optionIndex) => (
                  <div key={optionIndex} className="mb-2">
                    <label htmlFor={`option${optionIndex}`} className="form-label">
                      Option {optionIndex + 1}:
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id={`option${optionIndex}`}
                      value={option || ''}
                      onChange={(e) => handleEditOptionChange(index, optionIndex, e.target.value)}
                    />
                  </div>
                ))}
                <div className="mb-2">
                  <label htmlFor="correctAnswer" className="form-label">Correct Answer:</label>
                  <input
                    type="text"
                    className="form-control"
                    id="correctAnswer"
                    value={editQuestions[index]?.correctAnswer || ''}
                    onChange={(e) => handleEditQuestionChange(index, 'correctAnswer', e.target.value)}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setIsEditQuizModalOpen(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleUpdateQuiz}
          >
            Save Quiz
          </button>
        </div>
      </div>
    </div>
  </div>
)}

    {/* Edit Course Description Modal */}
    {isEditCourseDescriptionModalOpen && (
      <div className="modal" style={{ display: 'block' }}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Edit Course Description</h5>
<button type="button" class="close" data-dismiss="modal" aria-label="Close"
              onClick={() => setIsEditCourseDescriptionModalOpen(false)}>
              <span aria-hidden="true">×</span>
            </button>
            </div>
            <div className="modal-body">
              <div style={{ padding: '20px' }}>
                <div className="mb-3">
                  <label htmlFor="courseDescriptionEditor" className="form-label">Course Description:</label>
                  <Editor
                    editorState={editCourseDescriptionEditorState}
                    wrapperClassName="demo-wrapper"
                    editorClassName="demo-editor"
                    onEditorStateChange={setEditCourseDescriptionEditorState}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setIsEditCourseDescriptionModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleUpdateCourseDescription}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Add Presentation Modal */}
    {isPresentationModalOpen && (
    <div className="modal" style={{ display: 'block' }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Add New Presentation</h5>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close" onClick={closePresentationModal}>
              <span aria-hidden="true">×</span>
            </button>
          </div>
          <div className="modal-body">
            <div style={{ padding: '20px' }}>
              <div className="mb-3">
                <label htmlFor="presentationTitle" className="form-label">Presentation Title:</label>
                <input
                  type="text"
                  className="form-control"
                  id="presentationTitle"
                  placeholder="Enter presentation title"
                  value={newPresentationTitle}
                  onChange={(e) => setNewPresentationTitle(e.target.value)}
                  required
                />
              </div>

              {/* NEW: File Upload */}

              <div className="mb-3">
                <label className="form-label">Choose Presentation Source:</label>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="presentationSource"
                    id="sourceUpload"
                    value="upload"
                    checked={presentationSource === 'upload'}
                    onChange={() => setPresentationSource('upload')}
                  />
                  <label className="form-check-label" htmlFor="sourceUpload">
                    Upload New File
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="presentationSource"
                    id="sourceExisting"
                    value="existing"
                    checked={presentationSource === 'existing'}
                    onChange={() => setPresentationSource('existing')}
                  />
                  <label className="form-check-label" htmlFor="sourceExisting">
                    Select Existing File
                  </label>
                </div>
              </div>
              {presentationSource === 'upload' && (
  <div className="mb-3">
    <label htmlFor="presentationFile" className="form-label">
      Upload Presentation File (.pptx or .mp4):
    </label>
    <input
      type="file"
      className="form-control"
      id="presentationFile"
      accept=".ppt,.pptx,.mp4"
      onChange={handlePresentationFileUpload}
    />
    <small className="form-text text-muted">
      PowerPoint files will use Microsoft Office Viewer. MP4 files will embed as native video.
    </small>
  </div>
)}

{presentationSource === 'existing' && (
  <div className="mb-3">
  <label htmlFor="existingPresentationSelect" className="form-label fw-bold">
    🎞️ Select Existing Presentation:
  </label>
  <select
    className="form-control"
    id="existingPresentationSelect"
    onChange={handleExistingPresentationSelect}
  >
    <option value="">-- Choose a file --</option>
    {existingPresentations.map((file) => {
      const ext = file.name.split('.').pop().toLowerCase();
      const label = `${file.originalName || file.name}`;
      const typeBadge = ext === 'mp4' ? '🎥 Video' : '📊 PowerPoint';

      return (
        <option key={file._id || file.name} value={file.name}>
          {label} — {typeBadge}
        </option>
      );
    })}
  </select>
  <small className="form-text text-muted">
    Select a previously uploaded <strong>.pptx</strong> or <strong>.mp4</strong> file to auto-generate the embed code.
  </small>
</div>
)}

              {/* Embed Code Textarea */}
              <div className="mb-3">
                <label htmlFor="newPresentationEmbedCode" className="form-label">Presentation Embed Code:</label>
                <textarea
                  className="form-control"
                  id="newPresentationEmbedCode"
                  rows="5"
                  placeholder="Paste the full iframe embed code here or upload a .pptx file"
                  value={newPresentationEmbedCode}
                  onChange={(e) => setNewPresentationEmbedCode(e.target.value)}
                  required
                ></textarea>
                <small className="form-text text-muted">
                  This should be the full <code>&lt;iframe&gt;</code> tag or will be auto-filled after upload.
                </small>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={closePresentationModal}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleAddPresentation}
            >
              Save Presentation
            </button>
          </div>
        </div>
      </div>
    </div>
  )}

    {/* Edit Presentation Modal */}
    {isEditPresentationModalOpen && editingPresentation && (
      <div className="modal" style={{ display: 'block' }}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Edit Presentation</h5>
              <button type="button" class="close" data-dismiss="modal" aria-label="Close"
              onClick={() => setIsEditPresentationModalOpen(false)}>
              <span aria-hidden="true">×</span>
            </button>
            </div>
            <div className="modal-body">
              <div style={{ padding: '20px' }}>
                <div className="mb-3">
                  <label htmlFor="editPresentationTitle" className="form-label">Presentation Title:</label>
                  <input
                    type="text"
                    className="form-control"
                    id="editPresentationTitle"
                    placeholder="Enter presentation title"
                    value={editPresentationTitle}
                    onChange={(e) => setEditPresentationTitle(e.target.value)}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="editPresentationEmbedCode" className="form-label">Presentation Embed Code:</label>
                  <textarea
                    className="form-control"
                    id="editPresentationEmbedCode"
                    rows="5"
                    placeholder="Paste the full iframe embed code here (e.g., from Google Slides, YouTube, etc.)"
                    value={editPresentationEmbedCode}
                    onChange={(e) => setEditPresentationEmbedCode(e.target.value)}
                    required
                  ></textarea>
                  <small className="form-text text-muted">
                    This should be the full `&lt;iframe&gt;` tag provided by presentation hosting services.
                  </small>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setIsEditPresentationModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleUpdatePresentation}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

      {/* NEW: Edit Course Details Modal */}
      {isEditCourseDetailsModalOpen && (
        <div className="modal" style={{ display: 'block' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Course Details</h5>
                <button type="button" class="close" data-dismiss="modal" aria-label="Close"
              onClick={() => setIsEditCourseDetailsModalOpen(false)}>
              <span aria-hidden="true">×</span>
            </button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="editCourseTitle" className="form-label">Course Title:</label>
                  <input
                    type="text"
                    className="form-control"
                    id="editCourseTitle"
                    value={editCourseTitle}
                    onChange={(e) => setEditCourseTitle(e.target.value)}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="editCourseLicense" className="form-label">License:</label>
                  <select
                    className="form-select"
                    id="editCourseLicense"
                    value={editCourseLicense}
                    onChange={(e) => setEditCourseLicense(e.target.value)}
                    required
                  >
                    <option value="private">Private</option>
                    <option value="public">Public</option>
                    <option value="cc-by">CC BY</option>
                    <option value="cc-by-sa">CC BY-SA</option>
                    <option value="cc-by-nd">CC BY-ND</option>
                    <option value="cc-by-nc">CC BY-NC</option>
                    <option value="cc-by-nc-sa">CC BY-NC-SA</option>
                    <option value="cc-by-nc-nd">CC BY-NC-ND</option>
                  </select>
                </div>
                <div className="mb-3 form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="editCourseIsPublic"
                    checked={editCourseIsPublic}
                    onChange={(e) => setEditCourseIsPublic(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="editCourseIsPublic">Is Public?</label>
                </div>
                <div className="mb-3">
                  <label htmlFor="editCourseStartDate" className="form-label">Start Date:</label>
                  <input
                    type="date"
                    className="form-control"
                    id="editCourseStartDate"
                    value={editCourseStartDate}
                    onChange={(e) => setEditCourseStartDate(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="editCourseEndDate" className="form-label">End Date:</label>
                  <input
                    type="date"
                    className="form-control"
                    id="editCourseEndDate"
                    value={editCourseEndDate}
                    onChange={(e) => setEditCourseEndDate(e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setIsEditCourseDetailsModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleUpdateCourseDetails}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={2000} />
    </div>
  );
};

export default CoursesDetail;
